import { createSlice } from "@reduxjs/toolkit";
import { fetchInvites, acceptInvite, submitVideo, rejectInvite } from "../services/operations/inviteApi";

const initialState = {
  invites: [],
  selectedInvite: null,
  status: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
};

const inviteSlice = createSlice({
  name: "invites",
  initialState,
  reducers: {
    setSelectedInvite: (state, action) => {
      state.selectedInvite = action.payload;
      console.log("Selected invite set:", action.payload);
    },
    clearSelectedInvite: (state) => {
      state.selectedInvite = null;
      console.log("Selected invite cleared");
    },
    clearInvites: (state) => {
      state.invites = [];
      state.status = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch invites cases
      .addCase(fetchInvites.pending, (state) => {
        state.status = "loading";
        console.log("fetchInvites.pending: Setting status to loading");
      })
      .addCase(fetchInvites.fulfilled, (state, action) => {
        state.status = "succeeded";
        console.log("fetchInvites.fulfilled: Raw payload received:", action.payload);
        
        // Check if payload is an array
        if (!Array.isArray(action.payload)) {
          console.error("fetchInvites.fulfilled: Payload is not an array:", action.payload);
          state.invites = [];
          return;
        }
        
        // Transform the API response to match the expected format in the UI
        state.invites = action.payload.map(invite => {
          console.log("Processing invite:", invite);

          // Create a properly formatted invite object
          const formattedInvite = {
            id: invite.id || '',
            invite_id: invite.invite_id || invite.id || '',
            email: invite.email || '',
            campaign_id: invite.campaign_id || '',
            campaignTitle: invite.campaignTitle || 'Untitled Campaign',
            brandName: invite.brandName || 'Unknown Brand',
            payout: invite.payout || 0,
            deadline: invite.deadline || '2025-12-31',
            status: (invite.status || 'pending').toUpperCase(),
            placements: invite.placements || {
              in_content_ad_placements: [],
              placement_details: {}
            },
            instructions: invite.instructions || 'N/A',
            videoUrl: invite.videoUrl || '',
            verificationStatus: invite.verificationStatus || '',
            verificationMessage: invite.verificationMessage || '',
            resubmissionsLeft: invite.resubmissionsLeft !== undefined ? invite.resubmissionsLeft : 3,
            channel_id: invite.channel_id,
            channel_name: invite.channel_name,
            campaignAim: invite.campaignAim || '',
            usps: invite.usps || [],
            socialMediaPlatforms: invite.socialMediaPlatforms || [],
            audiencePersona: invite.audiencePersona || [],
            regions: invite.regions || [],
            audienceTarget: invite.audienceTarget || '',
            budget: invite.budget || 0,
            productLink: invite.productLink || '',
            contentUrl: invite.contentUrl || {}
          };

          console.log("Formatted invite:", formattedInvite);
          return formattedInvite;
        });
        
        console.log("Final invites array:", state.invites);
      })
      .addCase(fetchInvites.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
        console.error("fetchInvites.rejected: Error:", state.error);
      })
      
      // Accept invite cases
      .addCase(acceptInvite.pending, (state) => {
        state.status = "loading";
        console.log("acceptInvite.pending: Setting status to loading");
      })
      .addCase(acceptInvite.fulfilled, (state, action) => {
        const { inviteId } = action.payload;
        console.log("acceptInvite.fulfilled: Updating invite status for inviteId:", inviteId);
        // Update the invite status in the state
        const inviteIndex = state.invites.findIndex(invite => invite.id === inviteId);
        if (inviteIndex !== -1) {
          state.invites[inviteIndex].status = 'ACCEPTED';
          state.invites[inviteIndex].verificationStatus = '';
          state.invites[inviteIndex].videoUrl = '';
          state.invites[inviteIndex].verificationMessage = '';
        }
        state.status = "succeeded";
      })
      .addCase(acceptInvite.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
        console.error("acceptInvite.rejected: Error:", state.error);
      })
      
      // Submit video URL cases
      .addCase(submitVideo.pending, (state) => {
        state.status = "loading";
        console.log("submitVideo.pending: Setting status to loading");
      })
      .addCase(submitVideo.fulfilled, (state, action) => {
        const { inviteId, videoUrl } = action.payload;
        console.log("submitVideo.fulfilled: Updating invite for inviteId:", inviteId, "with videoUrl:", videoUrl);
        // Update the invite in the state
        const inviteIndex = state.invites.findIndex(invite => invite.id === inviteId);
        if (inviteIndex !== -1) {
          state.invites[inviteIndex].status = 'SUBMITTED';
          state.invites[inviteIndex].videoUrl = videoUrl;
          state.invites[inviteIndex].verificationStatus = 'UNDER_REVIEW';
        }
        state.status = "succeeded";
      })
      .addCase(submitVideo.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
        console.error("submitVideo.rejected: Error:", state.error);
      })
      
      // Reject invite cases
      .addCase(rejectInvite.pending, (state) => {
        state.status = "loading";
        console.log("rejectInvite.pending: Setting status to loading");
      })
      .addCase(rejectInvite.fulfilled, (state, action) => {
        const { inviteId } = action.payload;
        console.log("rejectInvite.fulfilled: Removing invite for inviteId:", inviteId);
        // Remove the rejected invite from the state
        state.invites = state.invites.filter(invite => invite.id !== inviteId);
        state.status = "succeeded";
      })
      .addCase(rejectInvite.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
        console.error("rejectInvite.rejected: Error:", state.error);
      });
  },
});

export const { setSelectedInvite, clearSelectedInvite, clearInvites } = inviteSlice.actions;
export default inviteSlice.reducer;
